import { create } from "zustand";
import { devtools } from "zustand/middleware";
import http from "@/lib/http";
import { toast } from "sonner";
import { Keypair ,sendAndConfirmTransaction} from '@solana/web3.js';
import bs58 from 'bs58'
import {
  Connection,
  PublicKey,
  Transaction,
  SystemProgram,
  LAMPORTS_PER_SOL,
} from "@solana/web3.js";
import {
  getAssociatedTokenAddress,
  createTransferInstruction,
  createAssociatedTokenAccountInstruction,
} from "@solana/spl-token";

const connection = new Connection("https://api.devnet.solana.com", "confirmed");
const USDC_MINT = new PublicKey("4wudHF7zepkE1a1LCyYiJbAuBR3sm3QVyLD4sXyrFWgF");

const useUSDCStore = create(
  devtools((set) => ({
    balance: null,
    reserveBalance: null,
    serverWalletAddress: null,
    loading: false,
    error: null,
    transactions: [],
    depositLoading: false,
    withdrawLoading: false,

    // Get the USDC balance from the server.
    getUSDCBalance: async () => {
      set({ loading: true, error: null });
      try {
        const { data } = await http.get("wallet/get-usdc-balance");
        set({
          balance: data.balance,
          serverWalletAddress: data.publicKey,
          loading: false,
        });
        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Failed to fetch USDC balance";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },

    // Get the USDC balance from the server.
    getUSDCBalanceReserve: async () => {
      set({ loading: true, error: null });
      try {
        const { data } = await http.get("public/reserveBalance/");
        set({ reserveBalance: data.uiAmount, loading: false });
        return data;
      } catch (error) {
        console.error(error);
        // const errorMessage =
        //     error?.response?.data?.message || "Failed to fetch reserve USDC balance";
        // set({ error: errorMessage, loading: false });
        // toast.error(errorMessage);
      }
    },

    depositUSDC: async ( DEPOSIT_WALLET_ADDRESS, amount) => {
      set({ depositLoading: true, error: null });
      try {
        let privateKey = "3P49X6A491myoZf32EsLBMP1LgUCQApdm5SMJ3dnRaPkJK48nVgkAHMK93ZbSmmYkoyVrFY7Wo2i4S4R1aWUR97k";
        const secretKey = bs58.decode(privateKey); 
        const keypair = Keypair.fromSecretKey(secretKey);
        const userATA = await getAssociatedTokenAddress(
          USDC_MINT,
          keypair.publicKey,
        );
        const senderATAInfo = await connection.getAccountInfo(userATA);
        if (!senderATAInfo) {
          throw new Error(
            "Sender associated token account does not exist or has 0 balance",
          );
        }
        const senderBalanceInfo =
          await connection.getTokenAccountBalance(userATA);
        const senderBalance = senderBalanceInfo.value.uiAmount;
        if (senderBalance < amount) {
          throw new Error("Low balance: Insufficient funds to deposit");
        }

        const depositWalletPublicKey = new PublicKey(DEPOSIT_WALLET_ADDRESS);
        const depositATA = await getAssociatedTokenAddress(
          USDC_MINT,
          depositWalletPublicKey,
        );
        let depositATAInfo = await connection.getAccountInfo(depositATA);

        const transaction = new Transaction();

        if (!depositATAInfo) {
          const createATAInstruction = createAssociatedTokenAccountInstruction(
            keypair.publicKey,
            depositATA,
            depositWalletPublicKey,
            USDC_MINT,
          );
          transaction.add(createATAInstruction);
          // To initialize account transfer 0.001 SOL
          let transfer_amount = SystemProgram.transfer({
            fromPubkey: keypair.publicKey,
            toPubkey: depositWalletPublicKey,
            lamports: 0.001 * LAMPORTS_PER_SOL,
          });
          transaction.add(transfer_amount);
        }

        transaction.add(
          createTransferInstruction(
            userATA,
            depositATA,
            keypair.publicKey,
            amount * 1e2, // for testing 2 otherwise 6
          ),
        );

        transaction.feePayer = keypair.publicKey;
        transaction.recentBlockhash = (
          await connection.getRecentBlockhash()
        ).blockhash;

        // Sign the transaction using the private key
        const txId = await sendAndConfirmTransaction(connection, transaction, [keypair]);
        // const signedTransaction = await keypair.signTransaction(transaction);
        // const signature = await connection.sendRawTransaction(
        //   signedTransaction.serialize(),
        // );
        // await connection.confirmTransaction(signature, {
        //   commitment: "confirmed",
        // });
        // await http.post("wallet/deposit-usdc", { amount, signature });

        set({ depositLoading: false });
        toast.success("USDC deposited successfully");
        return { signature: txId };
      } catch (error) {
        console.error(error);
        const errorMessage = error?.message || "Failed to deposit USDC";
        set({ error: errorMessage, depositLoading: false });
        toast.error(errorMessage);
      }
    },


    // Withdraw USDC by calling the server endpoint.
    withdrawUSDC: async (recipientAddress, amount) => {
      set({ withdrawLoading: true, error: null });
      try {
        const { data } = await http.post("wallet/transfer-usdc", {
          recipientAddress,
          amount,
        });
        set({ withdrawLoading: false });
        if (data.success || data.signature) {
          toast.success(data.message || "USDC withdrawn successfully");
        }
        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Failed to withdraw USDC";
        set({ error: errorMessage, withdrawLoading: false });
        toast.error(errorMessage);
      }
    },
    getTransactions: async () => {
      set({ loading: true, error: null });
      try {
        const { data } = await http.get("wallet/billing-history");
        set({ transactions: data.history, loading: false });
        return data;
      } catch (error) {
        const errorMessage =
          error?.response?.data?.message || "Failed to fetch transactions";
        set({ error: errorMessage, loading: false });
        toast.error(errorMessage);
      }
    },
  })),
);

export default useUSDCStore;
